import React, { useRef, useEffect, useState } from 'react';
import classNames from 'classnames';
import css from './FlatFilterMenu.module.css';

import { useHistory, useLocation } from 'react-router-dom';

const DISC_TYPES = ['Putt & Approach', 'Control Driver', 'Distance Driver', 'Mid Range'];
const OTHER_CATEGORIES = ['Accessories', 'Apparels & cloth', 'Footwear'];

/**
 * FlatFilterMenu
 *
 * A horizontally-scrollable pill-based category filter bar.
 * Writes the selected filter to the `flatFilter` URL query param.
 * Actual filtering is done upstream in EnhancedSearchPage via useMemo.
 */
const FlatFilterMenu = () => {
  const history = useHistory();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const categoryParam = searchParams.get('pub_categoryLevel1');
  const keywordParam = searchParams.get('keywords');

  let selectedFilter = 'All Listings';

  // Determine active pill from native Sharetribe params
  if (categoryParam === 'discs' && keywordParam && DISC_TYPES.includes(keywordParam)) {
    selectedFilter = keywordParam;
  } else if (categoryParam === 'footwear') {
    selectedFilter = 'Footwear';
  } else if (categoryParam === 'accessories') {
    selectedFilter = 'Accessories';
  } else if (categoryParam === 'apparelAndClothes') {
    selectedFilter = 'Apparels & cloth';
  } else if (!categoryParam && keywordParam && DISC_TYPES.includes(keywordParam)) {
    selectedFilter = keywordParam;
  }

  const [showMoreOthers, setShowMoreOthers] = useState(false);
  const scrollRef = useRef(null);

  // --- Drag-to-scroll (identical to ProfilePage.js) ---
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let isDown = false;
    let startX;
    let scrollLeft;
    let dragged = false;

    el.style.cursor = 'grab';

    const onMouseDown = e => {
      isDown = true;
      dragged = false;
      el.style.cursor = 'grabbing';
      startX = e.pageX - el.offsetLeft;
      scrollLeft = el.scrollLeft;
    };
    const onMouseLeave = () => { isDown = false; el.style.cursor = 'grab'; };
    const onMouseUp = () => { isDown = false; el.style.cursor = 'grab'; };
    const onClick = e => { if (dragged) { e.preventDefault(); e.stopPropagation(); } };
    const onMouseMove = e => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      const walk = (x - startX) * 2;
      if (Math.abs(walk) > 5) dragged = true;
      el.scrollLeft = scrollLeft - walk;
    };
    const onWheel = e => {
      if (e.deltaY === 0) return;
      const isAtLeft = el.scrollLeft === 0;
      const isAtRight = Math.abs(el.scrollLeft + el.clientWidth - el.scrollWidth) <= 1;
      if ((isAtLeft && e.deltaY < 0) || (isAtRight && e.deltaY > 0)) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };

    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('mouseleave', onMouseLeave);
    el.addEventListener('mouseup', onMouseUp);
    el.addEventListener('mousemove', onMouseMove);
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('click', onClick, { capture: true });

    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      el.removeEventListener('mouseleave', onMouseLeave);
      el.removeEventListener('mouseup', onMouseUp);
      el.removeEventListener('mousemove', onMouseMove);
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('click', onClick, { capture: true });
    };
  }, []);

  const handleSelect = value => {
    const params = new URLSearchParams(location.search);
    
    // Clear our managed params
    params.delete('pub_categoryLevel1');
    params.delete('keywords');
    params.delete('pub_discType'); // CRITICAL: remove the legacy param causing 400s
    params.delete('flatFilter'); // clean up old param if it exists
    params.delete('page'); // reset pagination on filter change

    if (value === 'All Listings') {
      // already cleared
    } else if (DISC_TYPES.includes(value)) {
      params.set('pub_categoryLevel1', 'discs');
      params.set('keywords', value);
    } else if (value === 'Footwear') {
      params.set('pub_categoryLevel1', 'footwear');
    } else if (value === 'Accessories') {
      params.set('pub_categoryLevel1', 'accessories');
    } else if (value === 'Apparels & cloth') {
      params.set('pub_categoryLevel1', 'apparelAndClothes');
    }

    history.push({ search: params.toString() });
  };

  return (
    <div className={css.filterMenuContainer}>
      <div className={css.filterPillScroll} ref={scrollRef}>
        {/* All */}
        <button
          className={classNames(css.filterPill, { [css.filterPillActive]: selectedFilter === 'All Listings' })}
          onClick={() => handleSelect('All Listings')}
        >
          All Listings
        </button>

        <div className={css.filterDivider} />

        {/* Disc types */}
        <span className={css.filterLabel}>Discs &gt;</span>
        {DISC_TYPES.map(type => (
          <button
            key={type}
            className={classNames(css.filterPill, { [css.filterPillActive]: selectedFilter === type })}
            onClick={() => handleSelect(type)}
          >
            {type}
          </button>
        ))}

        <div className={css.filterDivider} />

        {/* Other categories */}
        <span className={css.filterLabel}>Other &gt;</span>
        {OTHER_CATEGORIES.slice(0, showMoreOthers ? OTHER_CATEGORIES.length : 1).map(cat => (
          <button
            key={cat}
            className={classNames(css.filterPill, { [css.filterPillActive]: selectedFilter === cat })}
            onClick={() => handleSelect(cat)}
          >
            {cat}
          </button>
        ))}

        {!showMoreOthers && OTHER_CATEGORIES.length > 1 && (
          <button
            className={classNames(css.filterPill, css.filterActionPill)}
            onClick={() => setShowMoreOthers(true)}
          >
            +{OTHER_CATEGORIES.length - 1} More
          </button>
        )}

        {showMoreOthers && OTHER_CATEGORIES.length > 1 && (
          <button
            className={classNames(css.filterPill, css.filterActionPill)}
            onClick={() => setShowMoreOthers(false)}
          >
            Less
          </button>
        )}
      </div>
    </div>
  );
};

export default FlatFilterMenu;
